import React from 'react';

import DotIcon from '../../../assets/svg-components/thank-you/dot.svg';
import IllustrationPinkDots from '../../../assets/svg-components/thank-you/select_website_dots.svg';
import SGLogo from '../../../assets/svg-components/thank-you/siteground-logo.svg';
import IllustrationTriangles from '../../../assets/svg-components/thank-you/triangels.svg';
import { Button } from '../../../components/button/button';
import Checkbox from '../../../components/checkbox/checkbox';
import Input from '../../../components/input/input';
import Notice from '../../../components/notice/notice';
import { SectionContent } from '../../../components/section/section-content';
import { SectionHero } from '../../../components/section/section-hero';
import { SectionTitle } from '../../../components/section/section-title';
import ShareBox from '../../../components/share-box/share-box';
import { SocialMedia } from '../../../components/social-media-buttons/social-media';
import { Text } from '../../../components/text/text';
import { Title } from '../../../components/title/title';
import { Translate } from '../../../components/translate';
import { HTTP_REQUEST_FINISHED, HTTP_REQUEST_STARTED } from '../../../store/constants';
import { StoreContext } from '../../../store/store';
import ThankYouCard from './thank-you-card';
import './thank-you-section.scss';

export const EMAIL_REGEX =
  /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

interface ThankYouSectionProps {
  hideSignupForm?: boolean;
}

const DEFAULT_FORM_VALUES = {
  name: '',
  email: '',
  checked: '',
  recaptcha: ''
};

const setFieldErrors = ({ name, email, checked }) => {
  return {
    name: name === '' ? 'translate.thankyou.validation.notice.name' : '',
    email: !EMAIL_REGEX.test(email) ? 'translate.thankyou.validation.notice.email' : '',
    checked: !checked ? 'translate.thankyou.validation.notice.terms' : ''
  };
};

const hasError = (errors) => Object.values(errors).some((x) => x !== '');

const submitToNemo = ({ name, email, terms, mail_subscribe, r3k, url, sgUrl, onSuccess, onError }) => {
  const grecaptcha = (window as any).grecaptcha;
  grecaptcha.ready(function () {
    grecaptcha.execute(r3k, { action: 'payment' }).then(function (token) {
      fetch(url, {
        method: 'POST',
        body: JSON.stringify({
          rt: token,
          termsAgree: Number(terms),
          name,
          email,
          mail_subscribe,
          sg_site: sgUrl
        })
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status === 200) {
            onSuccess();
          } else {
            onError();
          }
        });
    });
  });
};

const ThankYouSection: React.FC<ThankYouSectionProps> = ({ hideSignupForm }) => {
  const [formSubmitted, setFormSubmitted] = React.useState(false);
  const { state, dispatch } = React.useContext(StoreContext);
  const { recaptchaToken: r3k, signUpUrl: url, sgUrl } = state?.config;

  const [formValues, setFormValues] = React.useState({
    name: '',
    email: '',
    checked: false,
    mail_subscribe: 1
  });

  const [error, setError] = React.useState<Partial<typeof DEFAULT_FORM_VALUES>>(DEFAULT_FORM_VALUES);

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormValues((prevState) => ({ ...prevState, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch({ type: HTTP_REQUEST_STARTED });

    const { name, email, checked, mail_subscribe } = formValues;
    const errors = setFieldErrors({ name, email, checked });

    if (hasError(errors)) {
      setError(errors);
      dispatch({ type: HTTP_REQUEST_FINISHED });
      return;
    }

    setError(DEFAULT_FORM_VALUES);

    const onSuccess = () => {
      setFormSubmitted(true);
      dispatch({ type: HTTP_REQUEST_FINISHED });
    };

    const onError = () => {
      setError({ recaptcha: 'translate.thankyou.validation.recaptcha.failed' });
      dispatch({ type: HTTP_REQUEST_FINISHED });
    };

    submitToNemo({ name, email, terms: checked, mail_subscribe, r3k, url, sgUrl, onSuccess, onError });
  };

  const formCardTitle = formSubmitted
    ? 'translate.thankyou.section.card.signup.title.form.submitted'
    : 'translate.thankyou.section.card.signup.title';

  return (
    <React.Fragment>
      <SectionHero className="rc-thankyou-section">
        <div className="rc-thankyou-section-hero__wrapper">
          <SectionTitle weight="bold">
            <Translate id="translate.thankyou.section.title" />
          </SectionTitle>

          <Text align="center" size="large">
            <Translate id="translate.thankyou.section.text" />
          </Text>
        </div>
      </SectionHero>
      <SectionContent className="rc-thankyou-section">
        {!hideSignupForm && (
          <ThankYouCard>
            <Title level="4" align="center" weight="bold">
              <Translate id={formCardTitle} />
            </Title>
            {!formSubmitted && (
              <form className="rc-form" onSubmit={handleSubmit}>
                <div className="rc-field-inputs">
                  <Input
                    value={formValues.name}
                    handleChange={handleFormChange}
                    name="name"
                    placeholder="John Smith"
                    label={<Translate id="translate.thankyou.section.input.name" />}
                    error={error.name !== ''}
                  />
                  <Input
                    value={formValues.email}
                    handleChange={handleFormChange}
                    name="email"
                    placeholder="youremail@domain.com"
                    label={<Translate id="translate.thankyou.section.input.email" />}
                    error={error.email !== ''}
                  />
                </div>

                <Checkbox checked={formValues.checked} handleChange={handleFormChange} />
                <Button tag="button" color="pink">
                  <Translate id="translate.thankyou.section.button" />
                </Button>
              </form>
            )}
            {hasError(error) && (
              <Notice title={<Translate id="translate.thankyou.validation.notice.title" />}>
                {Object.entries(error).map(
                  ([key, message]) =>
                    message && (
                      <div key={key} style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                        <DotIcon />
                        <Translate id={message} />
                      </div>
                    )
                )}
              </Notice>
            )}
            <IllustrationTriangles className="rc-thankyou__signup-card--illustration" />
          </ThankYouCard>
        )}

        <ThankYouCard>
          <Title level="4" align="center" weight="bold">
            <Translate id="translate.thankyou.section.share" />
          </Title>
          <ShareBox />
          <Title level="5" align="center" weight="bold">
            <Translate id="translate.thankyou.section.or" />
          </Title>
          <SocialMedia />
          <IllustrationPinkDots className="rc-thankyou__share-card--illustration" />
        </ThankYouCard>
        <div className="rc-thankyou-footer">
          <SGLogo className="rc-thankyou__siteground-logo" />
          <Text weight="bold" align="center">
            <Translate id="translate.thankyou.section.footer" />
          </Text>
        </div>
        <Text align="center" className="rc-thankyou-footer__recaptcha">
          <Translate id="translate.recaptcha.terms.description" customStyles={{ a: { color: '#98e5b9' } }} />
        </Text>
      </SectionContent>
    </React.Fragment>
  );
};

export default ThankYouSection;
